import { css } from "@emotion/react";
import React, { forwardRef } from "react";
import { AsComponentProp, ComponentWithAs } from "../AsComponentProps";
import { useTheme } from "../theme";

interface BaseListItemProps {
  _?: never;
}

export type ListItemProps<E extends React.ElementType> = AsComponentProp<
  E,
  BaseListItemProps
>;

export const ListItem = forwardRef(
  <E extends React.ElementType = "li">(
    { children, as, style, className, ...props }: ListItemProps<E>,
    ref: React.ForwardedRef<HTMLLIElement>,
  ) => {
    const {
      typography,
      palette,
      transitions: { duration, easing },
    } = useTheme();
    const Component = as ?? "li";
    return as !== undefined ? (
      <li
        ref={ref}
        css={css`
          border-bottom: 1px solid ${palette.gray[200]};
          &:last-of-type {
            border-bottom: none;
          }
        `}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        style={style}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        className={className}
      >
        <Component
          css={[
            typography.body,
            css`
              box-sizing: border-box;
              display: flex;
              align-items: center;
              width: auto;
              padding: 0.6em 1em;
              overflow: hidden;
              color: inherit;
              text-decoration: none;
              white-space: nowrap;
              vertical-align: text-bottom;
              cursor: pointer;
              transition: background-color ${duration.shortest} ${easing.sharp};
              &:hover {
                background-color: ${palette.gray[200]};
              }
              &:active {
                background-color: ${palette.gray[300]};
              }
            `,
          ]}
          {...props}
        >
          {children}
        </Component>
      </li>
    ) : (
      <li
        ref={ref}
        css={[
          typography.body,
          css`
            box-sizing: border-box;
            display: flex;
            align-items: center;
            width: auto;
            padding: 0.6em 1em;
            overflow: hidden;
            color: inherit;
            text-decoration: none;
            white-space: nowrap;
            vertical-align: text-bottom;
            cursor: pointer;
            border-bottom: 1px solid ${palette.gray[200]};
            transition: background-color ${duration.shortest} ${easing.sharp};
            &:hover {
              background-color: ${palette.gray[200]};
            }
            &:active {
              background-color: ${palette.gray[300]};
            }
            &:last-of-type {
              border-bottom: none;
            }
          `,
        ]}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        style={style}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        className={className}
        {...props}
      >
        {children}
      </li>
    );
  },
) as ComponentWithAs<"li", BaseListItemProps>;
